import React, { useEffect, useMemo } from 'react';
import {
    CallRequestNewContainerProps,
    CallRequestNewViewProps,
} from './callRequestNewProps';
import { ChatContainerBaseConfig } from '../../../base';
import {
    CallRequestNewController,
    CallRequestNewLoader,
} from './callRequestNewInterface';
import { assert } from '@varos/util-typescript';
import { createCallPreset } from '@varos/call-preset';
import { createAssistantSdk } from '@varos/assistant-sdk';
import { Call, CallContext } from '@varos/call-core';
import { EnvironmentVariables } from '../../../../../../../config/environment';
import { createChatStrategy } from '@varos/assistant-react-chat';
import { buildMessageContext } from '../../../../integration/page/thread/detail/integrationDetailFactory';

export function createCallRequestNewContainer(
    config: ChatContainerBaseConfig,
    loader: CallRequestNewLoader,
    controller: CallRequestNewController
): React.FC<CallRequestNewContainerProps> {
    const {
        context: { useContext },
        kernel: {
            infra: {
                toaster: { useToast },
            },
        },
        hook,
    } = config;

    const {
        call: { useCall },
    } = createCallPreset({
        debugIceServers: false,
        client: createAssistantSdk({
            baseUrl: EnvironmentVariables.ASSISTANT_API_BASE_URL,
        }),
        autoStart: true,
    });

    // Configure chat strategy with auto-reply enabled
    const { useChat } = createChatStrategy({
        autoReply: {
            enabled: true,
            // Use default condition: last message is from user AND no assistant messages exist
        },
    });

    return config.state.composer.subscribe(({ as: View, ...containerProps }) => {
        const context = useContext();
        const toast = useToast();
        const state = {
            composer: config.state.composer.useState(context),
        };

        const data = loader.useLoad(context, {
            integration: containerProps.integration,
            thread: containerProps.thread,
        });

        const auth = hook.useAuth();
        assert(auth.scheme.kind === 'legacy', 'legacy auth scheme is required');
        assert(auth.scheme.store.authToken, 'auth token is required');

        const chat = useChat(context, {
            options: {
                mode: 'solution',
                environmentMode: state.composer.value.test ? 'test' : 'live',
                tag: state.composer.value.version,
                workspace: context.workspace.id,
            },
            token: auth.scheme.store.authToken,
            messages: useMemo(
                () => data.thread.messages.map((msg) => msg.message),
                [data.thread.messages]
            ),
            threadState: 'preparing',
            threadId: data.thread.thread.id,
            onGenerated() {
                console.log('onGenerated');
            },
            async onMessageCreate(message) {
                console.log('onMessageCreate', message);
            },
            async onMessageUpdate(message) {
                throw new Error('Not implemented');
            },
            async onCreateDocument(args) {
                throw new Error('Not implemented');
            },
            async onReplaceDocument(args) {
                throw new Error('Not implemented');
            },
        });

        const hasRepliedRef = React.useRef(false);
        useEffect(() => {
            async function perform() {
                if (hasRepliedRef.current) return;
                hasRepliedRef.current = true;
                const result = await chat.reply({
                    context: buildMessageContext(data),
                    parts: [
                        {
                            type: 'text',
                            text: 'User requested a call with autosubmit enabled',
                        },
                    ],
                });
                console.log('result', result);

                const [callInvocation] = result.parts.flatMap((part) =>
                    part.type === 'tool-invocation' &&
                    part.toolInvocation.toolName === 'start_call'
                        ? [part.toolInvocation]
                        : []
                );

                assert(callInvocation, 'call invocation is required');

                const context: CallContext = {
                    transcript: callInvocation.args.transcript.messages,
                    configuration: {
                        interviewer: {
                            name: callInvocation.args.interviewer.name,
                            description: callInvocation.args.interviewer.description,
                        },
                        purpose: callInvocation.args.purpose,
                        context: callInvocation.args.context,
                        deliverables: callInvocation.args.deliverables,
                        questions: callInvocation.args.questions,
                    },
                    dump: callInvocation.args as unknown as Record<string, unknown>,
                };

                console.log('call context', context);

                return containerProps.onContext(context);
            }
            perform();
        }, [chat]);

        const props = controller.useProps(context, data, {});

        return <View {...props} />;
    });
}
